import { Skeleton } from "@qw/ui";

import { useConversation } from "../providers/use-conversation";
import { MessageBody } from "./message-body";
import { MessageClassification } from "./message-classification";
import { MessageFooter } from "./message-footer";
import { MessageHeader } from "./message-header";

export const MessageDetail = ({ messageId }: { messageId: string }) => {
  // The body comes from one conversation-level batched fetch rather than a query per message
  // (B-1824), so a thread costs one request and one provider thread fetch per mailbox copy,
  // however many messages render.
  const { displayHeaders, messageBodiesById, messageBodiesError, messageBodiesLoading } = useConversation();
  const inboxMessage = messageBodiesById.get(messageId);

  // Every fallback is gated on *this* message being absent rather than on the batch's state,
  // because the batch is shared across the thread: a refetch triggered by one newly arrived message
  // must not blank — or error out — the bodies already on screen. Apollo keeps the last successful
  // data through both a refetch and a refetch failure, so an on-screen body simply stays.
  if (!inboxMessage && messageBodiesLoading) {
    return (
      <div className="pb-6 px-(--gutter) space-y-2">
        <Skeleton className="flex-none h-4 w-8/9" />
        <Skeleton className="flex-none h-4 w-6/9" />
        <Skeleton className="flex-none h-4 w-7/9" />
      </div>
    );
  }

  if (!inboxMessage && messageBodiesError) {
    return (
      <div className="pb-6 px-(--gutter) space-y-3">
        <h3 className="text-destructive text-lg">Error</h3>
        <p className="text-sm text-muted-foreground">{messageBodiesError.message}</p>
      </div>
    );
  }

  if (!inboxMessage) {
    return (
      <div className="pb-6 px-(--gutter) space-y-3">
        <h3 className="text-lg">Message not found</h3>
        <p className="text-muted-foreground text-sm">
          This message may have been deleted or you don&apos;t have access to it
        </p>
      </div>
    );
  }

  const { id, classification } = inboxMessage;

  return (
    <div className="px-(--gutter) mb-6 text-sm">
      {displayHeaders && <MessageHeader message={inboxMessage} className="mb-5" />}
      <MessageBody message={inboxMessage} />
      <MessageClassification messageId={id} currentClassification={classification} />
      <MessageFooter message={inboxMessage} />
    </div>
  );
};
